import React, {useEffect, useMemo, useRef, useCallback} from 'react'
import {Link as RouterLink, Route, Switch, useLocation, useHistory} from 'react-router-dom'
import {matchPath} from 'react-router'
import {Routes} from '../../common/Routes'
import {filterUndefinedCoordinates} from '../../common/utils/filterUndefinedCoordinates'
import {Box} from '../../webui/Box'
import MuiBox from '@mui/material/Box'
import {Container} from '../../webui/Container'
import {Link} from '../../webui/Link'
import {Paper} from '../../webui/Paper'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {Typography} from '../../webui/Typography'
import {ActivityArea} from './ActivityArea'
import {ActivityAreaMap} from './ActivityAreasMap'
import {ActivityAreasTable} from './ActivityAreasTable'
import {useAllActivityAreas} from './gql/useActivityAreas'
import {ActivityAreaMarker} from './types/activityAreaMarker'

interface AllActivityAreasPageProps {}

export const AllActivityAreas: React.FC<AllActivityAreasPageProps> = () => {
  const {data: activityAreas, loading, error} = useAllActivityAreas()

  const {activeCount, inactiveCount, markers, tableData} = useMemo(() => {
    const list = activityAreas ?? []
    let active = 0
    let inactive = 0

    for (let i = 0; i < list.length; i++) {
      if (list[i].active) active++
      else inactive++
    }

    const rawMarkers = list.map((a) => ({
      active: a.active,
      coordinates: a.location?.coordinates,
      id: a.id,
      type: a.type,
    }))

    const markers = filterUndefinedCoordinates(rawMarkers) as ActivityAreaMarker[]
    return {
      activeCount: active,
      inactiveCount: inactive,
      markers,
      tableData: list,
    }
  }, [activityAreas])

  const location = useLocation()
  const history = useHistory()

  const activityTopRef = useRef<HTMLDivElement | null>(null)
  const tableWrapperRef = useRef<HTMLDivElement | null>(null)

  // --- Robust scroll-to-top that beats scroll restoration & late layout ---
  const getScrollableAncestors = (start?: Element | null): (Window | Element)[] => {
    const targets: (Window | Element)[] = []
    targets.push(window)
    if (document.scrollingElement) targets.push(document.scrollingElement)
    targets.push(document.documentElement, document.body)
    const root = document.getElementById('root')
    if (root) targets.push(root)
    let el: Element | null | undefined = start ?? activityTopRef.current ?? null
    while (el && el !== document.body && el !== document.documentElement) {
      const style = window.getComputedStyle(el)
      const overflowY = style.overflowY
      const canScroll = (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && el.scrollHeight > el.clientHeight
      if (canScroll) targets.push(el)
      el = el.parentElement
    }
    const seen = new Set<Element | Window>()
    return targets.filter((t) => {
      if (seen.has(t)) return false
      seen.add(t)
      return true
    })
  }

  const scrollTargetsToTop = (targets: (Window | Element)[], behavior: ScrollBehavior | 'instant' = 'auto') => {
    targets.forEach((t) => {
      try {
        if (t === window) {
          ;(t as Window).scrollTo({
            top: 0,
            left: 0,
            behavior: behavior as ScrollBehavior,
          })
        } else {
          ;(t as Element).scrollTop = 0
          ;(t as Element).scrollLeft = 0
        }
      } catch {}
    })
  }

  const forceScrollToTop = useCallback(() => {
    const targets = getScrollableAncestors()
    scrollTargetsToTop(targets, 'auto')
    requestAnimationFrame(() => scrollTargetsToTop(targets, 'auto'))
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollTargetsToTop(targets, 'auto'))
    })
    setTimeout(() => scrollTargetsToTop(getScrollableAncestors(), 'auto'), 50)
    setTimeout(() => scrollTargetsToTop(getScrollableAncestors(), 'auto'), 150)
    setTimeout(() => scrollTargetsToTop(getScrollableAncestors(), 'auto'), 300)
  }, [])

  // --- Class-driven flash that always restarts via reflow ---
  const playFlash = useCallback(() => {
    forceScrollToTop()
    const host = activityTopRef.current
    if (!host) return
    host.classList.remove('flash-active')
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    ;(host as HTMLElement).offsetWidth // reflow
    host.classList.add('flash-active')
    window.setTimeout(() => host.classList.remove('flash-active'), 1650)
  }, [forceScrollToTop])

  // Small throttle to avoid double-running when multiple hooks fire near-simultaneously
  const lastFlashTsRef = useRef(0)
  const playFlashThrottled = useCallback(() => {
    const now = Date.now()
    if (now - lastFlashTsRef.current < 120) return
    lastFlashTsRef.current = now
    playFlash()
  }, [playFlash])

  // Patch history so ANY navigation to /activity-area/:id triggers flash (even same URL)
  useEffect(() => {
    const base = Routes.ACTIVITY_AREA.replace(':id', '')
    const shouldFlashFor = (to: any) => {
      try {
        if (typeof to === 'string') {
          const url = new URL(to, window.location.origin)
          return url.pathname.startsWith(base)
        }
        if (to && typeof to === 'object') {
          const pathname = (to as {pathname?: string}).pathname ?? ''
          return pathname.startsWith(base)
        }
      } catch {}
      return false
    }

    const originalPush = history.push
    const originalReplace = history.replace

    // @ts-ignore
    history.push = (...args: any[]) => {
      if (shouldFlashFor(args[0])) {
        // defer so we don't preempt click/navigation
        setTimeout(() => window.dispatchEvent(new Event('activity-area-flash')), 0)
      }
      // @ts-ignore
      return originalPush.apply(history, args)
    }
    // @ts-ignore
    history.replace = (...args: any[]) => {
      if (shouldFlashFor(args[0])) {
        setTimeout(() => window.dispatchEvent(new Event('activity-area-flash')), 0)
      }
      // @ts-ignore
      return originalReplace.apply(history, args)
    }

    return () => {
      // @ts-ignore
      history.push = originalPush
      // @ts-ignore
      history.replace = originalReplace
    }
  }, [history])

  // Listen for our custom event (deferred)
  useEffect(() => {
    const handler = () => setTimeout(() => playFlashThrottled(), 0)
    window.addEventListener('activity-area-flash', handler as EventListener)
    return () => window.removeEventListener('activity-area-flash', handler as EventListener)
  }, [playFlashThrottled])

  // Document-level CLICK (capture): if clicking an <a> to /activity-area/:id, flash AFTER the click
  useEffect(() => {
    const base = Routes.ACTIVITY_AREA.replace(':id', '')
    const onClickCapture = (e: Event) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      let el: HTMLElement | null = target
      while (el && el !== document.body) {
        if (el.tagName === 'A') {
          const href = (el as HTMLAnchorElement).getAttribute('href') || (el as HTMLAnchorElement).href
          if (!href) break
          try {
            const url = href.startsWith('http') ? new URL(href) : new URL(href, window.location.origin)
            if (url.pathname.startsWith(base)) {
              // defer to let router process click first
              setTimeout(() => playFlashThrottled(), 0)
            }
          } catch {}
          break
        }
        el = el.parentElement
      }
    }
    document.addEventListener('click', onClickCapture, true)
    return () => document.removeEventListener('click', onClickCapture, true)
  }, [playFlashThrottled])

  // Run on real route changes (deep link, back/forward) -> post-navigation
  useEffect(() => {
    const match = matchPath<{id: string}>(location.pathname, {
      path: Routes.ACTIVITY_AREA,
      exact: false,
      strict: false,
    })
    if (match) setTimeout(() => playFlashThrottled(), 0)
  }, [location.pathname, playFlashThrottled])

  // Table wrapper CLICK (capture) — trigger on any tbody row press, even without links (deferred)
  const onTableClickCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      const row = target.closest('tbody tr')
      if (row && tableWrapperRef.current?.contains(row)) {
        setTimeout(() => playFlashThrottled(), 0)
      }
    },
    [playFlashThrottled],
  )

  // NEW: read selected id from route (for map highlight)
  const match = matchPath<{id: string}>(location.pathname, {
    path: Routes.ACTIVITY_AREA,
    exact: false,
    strict: false,
  })
  const selectedId = match?.params?.id

  if (loading) return <LoadingSpinnerContainer />
  if (error) return <div>{error.message}</div>

  return (
    <Container>
      {/* Flash wrapper (ref via MUI Box). We animate only the inner Paper to match exact edges. */}
      <MuiBox
        ref={activityTopRef}
        sx={{
          position: 'relative',
          '@keyframes flashTint': {
            '0%': {backgroundColor: 'rgba(0,77,64,0.12)'},
            '10%': {backgroundColor: 'rgba(0,77,64,0.20)'},
            '20%': {backgroundColor: 'transparent'},
            '35%': {backgroundColor: 'rgba(0,77,64,0.16)'},
            '50%': {backgroundColor: 'transparent'},
            '65%': {backgroundColor: 'rgba(0,77,64,0.14)'},
            '80%': {backgroundColor: 'transparent'},
            '100%': {backgroundColor: 'transparent'},
          },
          '@keyframes ringPulseFast': {
            '0%': {
              boxShadow: 'none',
              outline: '2px solid rgba(0,77,64,0.85)',
              opacity: 1,
            },
            '10%': {
              boxShadow: '0 0 0 6px rgba(0,77,64,0.35)',
              outlineColor: 'rgba(0,77,64,0.65)',
            },
            '20%': {
              boxShadow: 'none',
              outlineColor: 'rgba(0,77,64,0.0)',
              opacity: 0.6,
            },
            '35%': {
              boxShadow: '0 0 0 6px rgba(0,77,64,0.35)',
              outlineColor: 'rgba(0,77,64,0.65)',
              opacity: 1,
            },
            '50%': {
              boxShadow: 'none',
              outlineColor: 'rgba(0,77,64,0.0)',
              opacity: 0.6,
            },
            '65%': {
              boxShadow: '0 0 0 6px rgba(0,77,64,0.35)',
              outlineColor: 'rgba(0,77,64,0.65)',
              opacity: 1,
            },
            '80%': {
              boxShadow: 'none',
              outlineColor: 'rgba(0,77,64,0.0)',
              opacity: 0.6,
            },
            '100%': {
              boxShadow: 'none',
              outlineColor: 'rgba(0,77,64,0.0)',
              opacity: 0,
            },
          },
          '&.flash-active .MuiPaper-root:first-of-type': {
            position: 'relative',
            zIndex: 0,
            overflow: 'visible',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              animation: 'flashTint 1.6s ease-in-out',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              outline: '2px solid rgba(0,77,64,0.85)',
              outlineOffset: '0px',
              animation: 'ringPulseFast 1.6s ease-in-out',
            },
          },
        }}
      >
        <Switch>
          <Route path={Routes.ACTIVITY_AREA}>
            <ActivityArea />
          </Route>
        </Switch>
      </MuiBox>

      <Box p={2} display="flex" flexDirection="row" justifyContent="space-between">
        <Box>
          <Typography>
            {activeCount} active and {inactiveCount} inactive activity areas.
          </Typography>
        </Box>
        <Box>
          <Link component={RouterLink} to={Routes.ADD_ACTIVITY_AREA}>
            Add new activity area
          </Link>
        </Box>
      </Box>

      <Paper>
        {/* pass selectedId to map for highlight */}
        <ActivityAreaMap activityAreaMarkers={markers} selectedId={selectedId} />
      </Paper>

      {/* Table click capture (deferred) */}
      <Box ref={tableWrapperRef} onClickCapture={onTableClickCapture}>
        <Box mt={2} />
        <ActivityAreasTable activityAreas={tableData} />
      </Box>
    </Container>
  )
}
